import dynamic from 'next/dynamic';

import { Table } from '@natu/ui/Table';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  type SBProps,
  type Spacing,
  type StoryblokTable,
  resolveStoryblokStyles,
  sbEditable,
} from '../../../../utils';

const TableCaption = dynamic(() => import('@natu/ui/Table').then(mod => mod.TableCaption));

const TableHeader = dynamic(() => import('@natu/ui/Table').then(mod => mod.TableHeader));

const TableRow = dynamic(() => import('@natu/ui/Table').then(mod => mod.TableRow));

const TableHead = dynamic(() => import('@natu/ui/Table').then(mod => mod.TableHead));

const TableBody = dynamic(() => import('@natu/ui/Table').then(mod => mod.TableBody));

const TableCell = dynamic(() => import('@natu/ui/Table').then(mod => mod.TableCell));

type Theme = 'light' | 'light-muted' | 'dark' | 'dark-muted' | 'default';

interface ThemeStyles {
  cell: string;
  caption: string;
  row: string;
}

/**
 * The theme describes the surface the table sits on - `light` variants are meant for light
 * surfaces (white, azureGray, but also the coral and butterscotch ones, where light text
 * does not pass the contrast requirements), `dark` ones for vulcan and gray-900.
 *
 * `default` keeps everything coming from the `Table` primitives, and the header is never
 * themed, because it always sits on its own light background.
 *
 * The styles are important, because the `Table` primitives set their own colors.
 */
const themeVariants: Record<Theme, ThemeStyles> = {
  default: {
    cell: '',
    caption: '',
    row: '',
  },
  light: {
    cell: '!text-gray-900',
    caption: '!text-gray-600',
    row: '',
  },
  'light-muted': {
    cell: '!text-gray-700',
    caption: '!text-gray-500',
    row: '',
  },
  dark: {
    cell: '!text-white',
    caption: '!text-gray-300',
    row: '!border-white/20 hover:!bg-white/5',
  },
  'dark-muted': {
    cell: '!text-gray-300',
    caption: '!text-gray-400',
    row: '!border-white/20 hover:!bg-white/5',
  },
};

interface SBTableProps {
  table?: StoryblokTable;
  caption?: string;
  theme?: Theme;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
}

export const SBTable = ({ blok }: SBProps<SBTableProps>) => {
  const { caption, table, theme, mbMobile, mbTablet, mbDesktop, mtMobile, mtTablet, mtDesktop } =
    blok;

  if (!table) {
    return null;
  }

  const { tbody, thead } = table;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const themeStyles = themeVariants[theme || 'default'];

  if (!isArrayWithLength(thead) || !isArrayWithLength(tbody)) return null;

  return (
    <Table className={className} {...sbEditable(blok)}>
      {caption && <TableCaption className={themeStyles.caption}>{caption}</TableCaption>}
      {isArrayWithLength(thead) && (
        <TableHeader>
          <TableRow>
            {thead.map(item => (
              <TableHead key={item._uid}>{item.value}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
      )}
      {isArrayWithLength(tbody) && (
        <TableBody>
          {tbody.map(rowItem => (
            <TableRow key={rowItem._uid} className={themeStyles.row}>
              {rowItem.body?.map(cellItem => (
                <TableCell key={cellItem._uid} className={themeStyles.cell}>
                  {cellItem.value}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      )}
    </Table>
  );
};
